import { useId, useState, type FormEvent } from 'react';

import { Button } from '@/shared/ui/Button';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { Input } from '@/shared/ui/Input';

import type { Client } from './types';
import { useUpdateClient } from './useClients';

/**
 * The customer's email on the quote's customer card, and a way to fix it there.
 *
 * Sending the quote from the platform (contract v10.26) writes to this address by
 * default, and a customer typed into the ERP years ago often has none, or an old one.
 * Going to Clientes to add it and coming back is the trip that makes somebody send the
 * PDF from Thunderbird instead, so it is edited in place: the client's card is updated
 * (`PATCH /api/clients/{id}`), which is also what the next quote for them will use.
 */
export function ClientEmail({ client, disabled }: { client: Client; disabled?: boolean }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(client.email ?? '');
  const update = useUpdateClient(client.id);
  const inputId = useId();

  function start(): void {
    setValue(client.email ?? '');
    update.reset();
    setEditing(true);
  }

  function save(event: FormEvent): void {
    event.preventDefault();
    const email = value.trim();
    update.mutate({ email: email || null }, { onSuccess: () => setEditing(false) });
  }

  if (editing) {
    return (
      <form className="mt-2 flex flex-col gap-2" onSubmit={save}>
        <Input
          id={inputId}
          label="Correo del cliente"
          type="email"
          value={value}
          autoFocus
          autoComplete="off"
          onChange={(event) => setValue(event.target.value)}
        />
        {update.isError ? (
          <ErrorMessage error={update.error}>No se pudo guardar el correo</ErrorMessage>
        ) : null}
        <div className="flex gap-2">
          <Button type="submit" size="sm" disabled={update.isPending}>
            {update.isPending ? 'Guardando…' : 'Guardar correo'}
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(false)}>
            Cancelar
          </Button>
        </div>
      </form>
    );
  }

  return (
    <p className="mt-1 flex flex-wrap items-baseline gap-x-2 text-[13.5px] leading-5">
      {client.email ? (
        <span className="text-ink-2 break-all">Correo: {client.email}</span>
      ) : (
        <span className="text-warn">Sin correo: para enviarle el presupuesto hace falta uno.</span>
      )}
      {!disabled ? (
        <button type="button" className="link text-[13px]" onClick={start}>
          {client.email ? 'Cambiar correo' : 'Añadir correo'}
        </button>
      ) : null}
    </p>
  );
}
